import { browser } from '$app/environment';
import { replaceState } from '$app/navigation';
import { get } from 'svelte/store';
import { getStrictPathLocale } from './locale';
import { MODAL_ROUTE_SLUGS as BASE_MODAL_ROUTE_SLUGS } from './modal-route-slugs';
import { loggedIn } from './session-store';
import { closeThisModal, ModalDialog, modalStack, pushModal, stateOpenModal } from './uiStore';
/** Query param key used for URL-driven modals. */
export const MODAL_QUERY_PARAM = 'modal';
export const DEFAULT_MODAL_ROUTES = [
    { slug: 'login', modal: ModalDialog.Auth, data: { tab: 'login' }, audience: 'loggedOut' },
    { slug: 'signup', modal: ModalDialog.Auth, data: { tab: 'signup' }, audience: 'loggedOut' },
    { slug: 'search', modal: ModalDialog.Search, useLegacyOpen: true },
    { slug: 'cashier', modal: ModalDialog.Cashier, audience: 'loggedIn' },
    // Legacy modals: internally check `stateOpenModal` only — require useLegacyOpen.
    { slug: 'lucky-dip', modal: ModalDialog.LuckyDip, audience: 'loggedIn', useLegacyOpen: true },
    { slug: 'profile', modal: ModalDialog.ProfileModal, audience: 'loggedIn', useLegacyOpen: true },
    { slug: 'rewards', modal: ModalDialog.RewardsDetails, audience: 'loggedIn', useLegacyOpen: true },
    { slug: 'freebets', modal: ModalDialog.Freebets, audience: 'loggedIn', useLegacyOpen: true },
];
const modalRouteBySlug = new Map(DEFAULT_MODAL_ROUTES.map((def) => [def.slug.toLowerCase(), def]));
let activeModalRouteSlug = null;
/** Mutable list used by param matchers; seeded from the static list in `modal-route-slugs.ts`. */
export const MODAL_ROUTE_SLUGS = [...BASE_MODAL_ROUTE_SLUGS];
/** Register or override a modal deep link (e.g. brand-specific campaign slug). */
export const registerModalRoute = (definition) => {
    const slug = definition.slug.toLowerCase();
    modalRouteBySlug.set(slug, { ...definition, slug });
    if (!MODAL_ROUTE_SLUGS.includes(slug)) {
        MODAL_ROUTE_SLUGS.push(slug);
    }
};
export const getModalRouteDefinition = (slug) => modalRouteBySlug.get(slug.toLowerCase());
// ─── Audience guard ──────────────────────────────────────────────────────────
const audienceAllows = (audience) => {
    const rule = audience ?? 'always';
    const isLoggedIn = get(loggedIn) === true;
    if (rule === 'loggedIn')
        return isLoggedIn;
    if (rule === 'loggedOut')
        return !isLoggedIn;
    return true;
};
// ─── Stack helpers ───────────────────────────────────────────────────────────
const isModalOpenForDefinition = (def, stack) => {
    if (def.useLegacyOpen)
        return get(stateOpenModal) === def.modal;
    return stack.some((e) => e.modal === def.modal);
};
const openModalFromDefinition = (def) => {
    if (!audienceAllows(def.audience))
        return;
    if (def.useLegacyOpen) {
        stateOpenModal.set(def.modal);
        return;
    }
    pushModal(def.modal, def.data);
};
const closeModalFromDefinition = (def) => {
    if (def.useLegacyOpen) {
        if (get(stateOpenModal) === def.modal)
            stateOpenModal.set(undefined);
        return;
    }
    closeThisModal(def.modal);
};
// ─── Slug resolution (query param + path fallback) ───────────────────────────
const PUBLIC_LOCALE_PREFIX = /^\/(cs|de|en|es|fr|it|ja|ko|pt|ru|zh)(?=\/|$)/;
const stripBase = (pathname, base) => {
    if (!base)
        return pathname || '/';
    if (pathname === base || pathname.startsWith(`${base}/`)) {
        return pathname.slice(base.length) || '/';
    }
    return pathname || '/';
};
const stripLocale = (path) => {
    const without = path.replace(PUBLIC_LOCALE_PREFIX, '');
    return without === '' ? '/' : without.startsWith('/') ? without : `/${without}`;
};
/**
 * Extract a recognized slug from a URL.
 * Priority: `?modal=<slug>` query param → single-segment path `/signup/`.
 */
export const parseModalRouteSlug = (url, base = '') => {
    // 1. Check query param first
    const paramSlug = url.searchParams.get(MODAL_QUERY_PARAM)?.toLowerCase();
    if (paramSlug && modalRouteBySlug.has(paramSlug))
        return paramSlug;
    // 2. Fallback: single-segment path (for external marketing URLs like /signup/)
    const stripped = stripLocale(stripBase(url.pathname, base));
    if (!stripped.startsWith('/'))
        return null;
    const normalized = stripped.endsWith('/') ? stripped : `${stripped}/`;
    const match = normalized.match(/^\/([^/]+)\/$/);
    if (!match)
        return null;
    const pathSlug = match[1].toLowerCase();
    return modalRouteBySlug.has(pathSlug) ? pathSlug : null;
};
export const isModalRouteUrl = (url, base = '') => parseModalRouteSlug(url, base) !== null;
/** @deprecated Pass a `URL` object. Kept for sites that still pass `pathname`. */
export const isModalRoutePathname = (pathname, base = '') => {
    try {
        const url = new URL(pathname, 'http://x');
        return isModalRouteUrl(url, base);
    }
    catch {
        return false;
    }
};
// ─── href helpers ────────────────────────────────────────────────────────────
/**
 * Returns a relative href that opens a modal by adding `?modal=<slug>` to `currentPath`.
 * The underlying page stays visible. Pass `locale` to prepend `/lang` when on a path route.
 */
export const getModalRouteHref = (slug, options) => {
    const base = options?.currentPath ?? '/';
    const path = base.endsWith('/') ? base.slice(0, -1) : base;
    const param = `${MODAL_QUERY_PARAM}=${slug.toLowerCase()}`;
    const separator = path.includes('?') ? '&' : '?';
    return `${path}${separator}${param}`;
};
// ─── URL management ──────────────────────────────────────────────────────────
const removeModalParam = (url) => {
    const next = new URL(url.href);
    next.searchParams.delete(MODAL_QUERY_PARAM);
    return next.pathname + (next.search || '') + (next.hash || '');
};
const replaceUrlIfNeeded = (href) => {
    if (!browser)
        return;
    const current = window.location.pathname + window.location.search + window.location.hash;
    if (current === href)
        return;
    replaceState(href, {});
};
// ─── Navigation handler ──────────────────────────────────────────────────────
/**
 * Call on every navigation (and on mount) with the current `URL`.
 * Opens the modal when a recognised slug is present; closes it when leaving.
 * Returns `true` when a modal route was matched.
 */
export const handleModalRouteNavigation = (url, base = '') => {
    const slug = parseModalRouteSlug(url, base);
    if (!slug) {
        if (activeModalRouteSlug) {
            const previous = getModalRouteDefinition(activeModalRouteSlug);
            if (previous)
                closeModalFromDefinition(previous);
            activeModalRouteSlug = null;
        }
        return false;
    }
    const def = getModalRouteDefinition(slug);
    if (!def)
        return false;
    activeModalRouteSlug = slug;
    openModalFromDefinition(def);
    return true;
};
// ─── Dismiss sync ────────────────────────────────────────────────────────────
const syncDismissUrl = (getUrl) => {
    if (!activeModalRouteSlug)
        return;
    const def = getModalRouteDefinition(activeModalRouteSlug);
    if (!def) {
        activeModalRouteSlug = null;
        return;
    }
    const stack = get(modalStack);
    if (isModalOpenForDefinition(def, stack))
        return;
    // Modal was closed — remove the ?modal= param (or restore path to locale-aware root)
    const url = getUrl();
    const slug = url.searchParams.get(MODAL_QUERY_PARAM)?.toLowerCase();
    if (slug === activeModalRouteSlug) {
        replaceUrlIfNeeded(removeModalParam(url));
    }
    else {
        // Path-based entry (/signup/) — navigate back to locale root
        const locale = getStrictPathLocale(url.pathname);
        const root = locale ? `/${locale}/` : '/';
        replaceUrlIfNeeded(root);
    }
    activeModalRouteSlug = null;
};
/**
 * Subscribe once in the app shell: cleans up the URL when a route-driven modal closes,
 * and re-attempts audience-gated modals once auth resolves (e.g. `?modal=cashier` while
 * `loggedIn` was still `undefined` on mount).
 * Returns an unsubscribe function; call it in the `onMount` cleanup.
 */
export const initModalRouteDismissSync = (getUrl, base = '') => {
    const unsubStack = modalStack.subscribe(() => syncDismissUrl(getUrl));
    const unsubLegacy = stateOpenModal.subscribe(() => syncDismissUrl(getUrl));
    // Re-try when auth resolves: covers the race where loggedIn was undefined at mount
    // and audienceAllows() returned false for 'loggedIn' or 'loggedOut' modals.
    let prevLoggedIn = get(loggedIn);
    const unsubAuth = loggedIn.subscribe((value) => {
        if (value === prevLoggedIn)
            return;
        prevLoggedIn = value;
        const url = getUrl();
        const slug = parseModalRouteSlug(url, base);
        if (!slug)
            return;
        const def = getModalRouteDefinition(slug);
        if (!def || !def.audience || def.audience === 'always')
            return;
        // Audience changed — re-evaluate
        if (audienceAllows(def.audience)) {
            activeModalRouteSlug = slug;
            openModalFromDefinition(def);
        }
        else {
            // No longer eligible (e.g. logged in user hit ?modal=signup) — clean URL
            replaceUrlIfNeeded(removeModalParam(url));
            activeModalRouteSlug = null;
        }
    });
    return () => {
        unsubStack();
        unsubLegacy();
        unsubAuth();
    };
};
